/* istanbul ignore file */

/* tslint:disable */

/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
import type { BandwidthOption } from '../models/BandwidthOption';
import type { BerthAssignment } from '../models/BerthAssignment';
import type { BerthDmaCaseSafetyPrediction } from '../models/BerthDmaCaseSafetyPrediction';
import type { BerthSafetyDailyWarnings } from '../models/BerthSafetyDailyWarnings';
import type { BerthSafetyResponse } from '../models/BerthSafetyResponse';
import type { BerthSafetyWarningDetails } from '../models/BerthSafetyWarningDetails';
import type { DmaCase } from '../models/DmaCase';
import type { DmaCaseBaseOfSimulation } from '../models/DmaCaseBaseOfSimulation';
import type { LoadingCondition } from '../models/LoadingCondition';
import type { MooredVessel } from '../models/MooredVessel';
import type { MooringLineLoad } from '../models/MooringLineLoad';
import type { MotionType } from '../models/MotionType';
import type { SafetyPredictionTrigger } from '../models/SafetyPredictionTrigger';
import type { SafetyPredictionsResponse } from '../models/SafetyPredictionsResponse';
import type { VesselDisposition } from '../models/VesselDisposition';

export class SafetyPredictionService {
  /**
   * Get base of simulation
   * Get the base of simulation for a DMA case. This information is used as basis for the Dynamic Mooring Analysis.
   * Information like the wind area, fender type, line type etc.
   *
   * @param xSelectedOrganisationId
   * @param berthId
   * @param dmaCaseIdentifier
   * @returns DmaCaseBaseOfSimulation OK
   * @throws ApiError
   */
  public static getBaseOfSimulation(
    xSelectedOrganisationId: string,
    berthId: string,
    dmaCaseIdentifier: string
  ): CancelablePromise<DmaCaseBaseOfSimulation> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/{berthId}/dma-cases/{dmaCaseIdentifier}/base-of-simulation',
      path: {
        berthId: berthId,
        dmaCaseIdentifier: dmaCaseIdentifier,
      },
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get the mooring line loads of one line force prediction
   * The load of every mooring line in the DMA matrix row a line force prediction selected (its simulationId),
   * as a percentage of the line's MBL and in kN.
   *
   * @param xSelectedOrganisationId
   * @param berthId
   * @param dmaCaseIdentifier
   * @param loadingCondition
   * @param disposition
   * @param simulationId
   * @returns MooringLineLoad OK
   * @throws ApiError
   */
  public static getMooringLineLoads(
    xSelectedOrganisationId: string,
    berthId: string,
    dmaCaseIdentifier: string,
    loadingCondition: LoadingCondition,
    disposition: VesselDisposition,
    simulationId: string
  ): CancelablePromise<Array<MooringLineLoad>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/{berthId}/dma-cases/{dmaCaseIdentifier}/line-loads',
      path: {
        berthId: berthId,
        dmaCaseIdentifier: dmaCaseIdentifier,
      },
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        loadingCondition: loadingCondition,
        disposition: disposition,
        simulationId: simulationId,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get berths
   * Retrieve the berths for the selected organisation.
   * When a user has access to multiple organisations the organisation passed in the header is used to retrieve the berths for.
   * The information can be retrieved for a number of days and for a specific start date.
   * When no historic view is needed specify the current time rounded to the hour.
   * When no numberOfDays is specified, 1 day is used.
   *
   * @param xSelectedOrganisationId
   * @param startTime
   * @param numberOfDays
   * @returns BerthSafetyResponse OK
   * @throws ApiError
   */
  public static getBerths(
    xSelectedOrganisationId: string,
    startTime: string,
    numberOfDays?: number
  ): CancelablePromise<Array<BerthSafetyResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/berths',
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        startTime: startTime,
        numberOfDays: numberOfDays,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get berths safety day by day within a specific start and end time
   * Retrieve daily safety warnings for all berths in a specific organisation within a date range.
   * When a user has access to multiple organisations the organisation passed in the header is used to retrieve the berths for.
   * The information can be retrieved for a specific start date and end date.
   * When no historic view is needed specify the current time rounded to the hour.
   * When no end date is not specified, it is equal to start day + 1 day.
   * The results are collected each day containing possible warnings for both simulated scenario and for vessels detected by AIS
   *
   * @param xSelectedOrganisationId
   * @param startDate
   * @param endDate
   * @returns BerthSafetyDailyWarnings OK
   * @throws ApiError
   */
  public static getDailyBerthsSafetyWorstWarnings(
    xSelectedOrganisationId: string,
    startDate: string,
    endDate?: string
  ): CancelablePromise<Array<BerthSafetyDailyWarnings>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/historic/berths/daily-warnings-summary',
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        startDate: startDate,
        endDate: endDate,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get moored vessel/dmaCase safety warnings for berths in a specific date
   * Retrieve the berths for the selected organisation.
   * When a user has access to multiple organisations the organisation passed in the header is used to retrieve the berths for.
   * When a berth is not specified, all berths within the requested organisation will be considered.
   * The information can be retrieved within the start and end of a specific date.
   * The results collected containing possible warnings for both simulated scenario and for vessels detected by AIS
   *
   * @param xSelectedOrganisationId
   * @param date
   * @param berthIds
   * @param trigger
   * @param withAisVesselsOnly
   * @returns BerthSafetyWarningDetails OK
   * @throws ApiError
   */
  public static getBerthsSafetyWarningsByDate(
    xSelectedOrganisationId: string,
    date: string,
    berthIds?: Array<string>,
    trigger?: SafetyPredictionTrigger,
    withAisVesselsOnly?: boolean
  ): CancelablePromise<Array<BerthSafetyWarningDetails>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/historic/berths/warnings-by-date',
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        date: date,
        berthIds: berthIds,
        trigger: trigger,
        withAISVesselsOnly: withAisVesselsOnly,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get berth safety prediction
   * Retrieve the berth with it's safety prediction for the selected organisation.
   * When a user has access to multiple organisations the organisation passed in the header is used to retrieve the berth for.
   *
   * @param xSelectedOrganisationId
   * @param berthIdentifier
   * @returns BerthAssignment OK
   * @throws ApiError
   */
  public static getBerth(xSelectedOrganisationId: string, berthIdentifier: string): CancelablePromise<BerthAssignment> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/berths/{berthIdentifier}',
      path: {
        berthIdentifier: berthIdentifier,
      },
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get assigned DMA cases
   * Get the assigned DMA cases for this berth and the specified organisation.
   * @param xSelectedOrganisationId
   * @param berthIdentifier
   * @returns DmaCase OK
   * @throws ApiError
   */
  public static getBerthDmaCases(xSelectedOrganisationId: string, berthIdentifier: string): CancelablePromise<Array<DmaCase>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/berths/{berthIdentifier}/dma-cases',
      path: {
        berthIdentifier: berthIdentifier,
      },
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get assigned DMA cases with the maximum prediction for the requested period
   * @param xSelectedOrganisationId
   * @param berthIdentifier
   * @param startTime
   * @param numberOfDays
   * @returns BerthDmaCaseSafetyPrediction OK
   * @throws ApiError
   */
  public static getBerthDmaCasesWithPrediction(
    xSelectedOrganisationId: string,
    berthIdentifier: string,
    startTime: string,
    numberOfDays?: number
  ): CancelablePromise<Array<BerthDmaCaseSafetyPrediction>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/berths/{berthIdentifier}/dma-cases/prediction',
      path: {
        berthIdentifier: berthIdentifier,
      },
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        startTime: startTime,
        numberOfDays: numberOfDays,
      },
    });
  }

  /**
   * Get line force safety predictions
   * Retrieve the safety predictions for line forces for the selected DMA case, loading condition and disposition.
   * @param xSelectedOrganisationId
   * @param berthIdentifier
   * @param dmaCaseId
   * @param loadingCondition
   * @param disposition
   * @param slotSize The slot size in hours.
   * @param days The number of days to look ahead. 1 by default.
   * @param dateTime
   * @param selectedBandwidth The selected bandwidth.
   * @returns SafetyPredictionsResponse OK
   * @throws ApiError
   */
  public static predictLineForce(
    xSelectedOrganisationId: string,
    berthIdentifier: string,
    dmaCaseId: string,
    loadingCondition: LoadingCondition,
    disposition: VesselDisposition,
    slotSize?: number,
    days?: number,
    dateTime?: string,
    selectedBandwidth?: BandwidthOption
  ): CancelablePromise<SafetyPredictionsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/berths/{berthIdentifier}/line-force',
      path: {
        berthIdentifier: berthIdentifier,
      },
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        dmaCaseId: dmaCaseId,
        loadingCondition: loadingCondition,
        disposition: disposition,
        slotSize: slotSize,
        days: days,
        dateTime: dateTime,
        selectedBandwidth: selectedBandwidth,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get fender deflection safety predictions
   * Retrieve the safety predictions for fender deflections for the selected DMA case, loading condition and disposition.
   * @param xSelectedOrganisationId
   * @param berthIdentifier
   * @param dmaCaseId
   * @param loadingCondition
   * @param disposition
   * @param slotSize The slot size in hours.
   * @param days The number of days to look ahead. 1 by default.
   * @param dateTime
   * @param selectedBandwidth The selected bandwidth.
   * @returns SafetyPredictionsResponse OK
   * @throws ApiError
   */
  public static predictFenderDeflection(
    xSelectedOrganisationId: string,
    berthIdentifier: string,
    dmaCaseId: string,
    loadingCondition: LoadingCondition,
    disposition: VesselDisposition,
    slotSize?: number,
    days?: number,
    dateTime?: string,
    selectedBandwidth?: BandwidthOption
  ): CancelablePromise<SafetyPredictionsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/berths/{berthIdentifier}/fender-deflection',
      path: {
        berthIdentifier: berthIdentifier,
      },
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        dmaCaseId: dmaCaseId,
        loadingCondition: loadingCondition,
        disposition: disposition,
        slotSize: slotSize,
        days: days,
        dateTime: dateTime,
        selectedBandwidth: selectedBandwidth,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get motion safety predictions
   * Retrieve the safety predictions for motions for the selected DMA case, loading condition and disposition.
   * @param xSelectedOrganisationId
   * @param berthIdentifier
   * @param motionType
   * @param dmaCaseId
   * @param loadingCondition
   * @param disposition
   * @param slotSize The slot size in hours.
   * @param days The number of days to look ahead. 1 by default.
   * @param dateTime
   * @param selectedBandwidth The selected bandwidth.
   * @returns SafetyPredictionsResponse OK
   * @throws ApiError
   */
  public static predictMotion(
    xSelectedOrganisationId: string,
    berthIdentifier: string,
    motionType: MotionType,
    dmaCaseId: string,
    loadingCondition: LoadingCondition,
    disposition: VesselDisposition,
    slotSize?: number,
    days?: number,
    dateTime?: string,
    selectedBandwidth?: BandwidthOption
  ): CancelablePromise<SafetyPredictionsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/berths/{berthIdentifier}/motion/{motionType}',
      path: {
        berthIdentifier: berthIdentifier,
        motionType: motionType,
      },
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        dmaCaseId: dmaCaseId,
        loadingCondition: loadingCondition,
        disposition: disposition,
        slotSize: slotSize,
        days: days,
        dateTime: dateTime,
        selectedBandwidth: selectedBandwidth,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get bollard forces safety predictions
   * Retrieve the safety predictions for bollard forces for the current berth and selected DMA case, loading condition and disposition.
   * Note this is not organisation dependent, since the bollard forces are registered with the berth, not the berth assignment
   *
   * @param xSelectedOrganisationId
   * @param berthIdentifier
   * @param dmaCaseId
   * @param loadingCondition
   * @param disposition
   * @param slotSize The slot size in hours.
   * @param days The number of days to look ahead. 1 by default.
   * @param dateTime
   * @param selectedBandwidth The selected bandwidth.
   * @returns SafetyPredictionsResponse OK
   * @throws ApiError
   */
  public static predictBollardForces(
    xSelectedOrganisationId: string,
    berthIdentifier: string,
    dmaCaseId: string,
    loadingCondition: LoadingCondition,
    disposition: VesselDisposition,
    slotSize?: number,
    days?: number,
    dateTime?: string,
    selectedBandwidth?: BandwidthOption
  ): CancelablePromise<SafetyPredictionsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/berths/{berthIdentifier}/bollard-forces',
      path: {
        berthIdentifier: berthIdentifier,
      },
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        dmaCaseId: dmaCaseId,
        loadingCondition: loadingCondition,
        disposition: disposition,
        slotSize: slotSize,
        days: days,
        dateTime: dateTime,
        selectedBandwidth: selectedBandwidth,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get moored vessels
   * Retrieve the currently moored vessels. That means all vessels that do not have a departure time (yet).
   * @param xSelectedOrganisationId
   * @param dateTime
   * @param predictionDays number of days should the safety prediction be in advance.
   * @returns MooredVessel OK
   * @throws ApiError
   */
  public static getMooredVessels(
    xSelectedOrganisationId: string,
    dateTime?: string,
    predictionDays?: number
  ): CancelablePromise<Array<MooredVessel>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/moored-vessels',
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        dateTime: dateTime,
        predictionDays: predictionDays,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get moored vessels for berth
   * Retrieve the currently moored vessels for a specific berth. That means all vessels that do not have a departure time (yet).
   * @param xSelectedOrganisationId
   * @param berthIdentifier
   * @param dateTime
   * @param days time span in which moored vessels should be queried.
   * @returns MooredVessel OK
   * @throws ApiError
   */
  public static getMooredVesselsForBerth(
    xSelectedOrganisationId: string,
    berthIdentifier: string,
    dateTime?: string,
    days?: number
  ): CancelablePromise<Array<MooredVessel>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/berths/{berthIdentifier}/moored-vessels',
      path: {
        berthIdentifier: berthIdentifier,
      },
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        dateTime: dateTime,
        days: days,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }

  /**
   * Get moored vessel for berth
   * Retrieve the currently moored vessel for a specific berth. That means all vessels that do not have a departure time (yet).
   * @param xSelectedOrganisationId
   * @param berthIdentifier
   * @param mmsi
   * @param dateTime
   * @returns MooredVessel OK
   * @throws ApiError
   */
  public static getMooredVesselForBerth(
    xSelectedOrganisationId: string,
    berthIdentifier: string,
    mmsi: string,
    dateTime?: string
  ): CancelablePromise<MooredVessel> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/safety-prediction/berths/{berthIdentifier}/moored-vessels/{mmsi}',
      path: {
        berthIdentifier: berthIdentifier,
        mmsi: mmsi,
      },
      headers: {
        'X-Selected-Organisation-Id': xSelectedOrganisationId,
      },
      query: {
        dateTime: dateTime,
      },
      errors: {
        400: `An error occurred caused by invalid content sent to our application.`,
        401: `Not authorized to see this content`,
        500: `Some unexpected error occurred, probably caused by either a configuration error or a programming mistake. Please contact the development team!`,
      },
    });
  }
}
